import { useState } from "react";
import { api, type ManagedMember } from "../lib/api";
import Modal from "./Modal";
import { useConfirm } from "./ConfirmProvider";

const SWATCHES = [
  "#17B886", "#12B76A", "#2E90FA", "#7F56D9", "#F79009",
  "#F04438", "#667085", "#EF6820", "#EE46BC", "#0BA5EC",
];

// Add or edit a person and their login. On edit, the password field is left
// blank to keep the current one, or filled to reset it.
export default function MemberModal({
  member,
  onClose,
  onSaved,
}: {
  member: ManagedMember | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = member != null;
  const [name, setName] = useState(member?.name ?? "");
  const [colour, setColour] = useState(member?.colour ?? "#17B886");
  const [username, setUsername] = useState(member?.username ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) return setError("Give them a name");
    if (!username.trim()) return setError("Give them a username to sign in with");
    if (!editing && password.length < 8) return setError("Password must be at least 8 characters");
    if (password && password.length < 8) return setError("Password must be at least 8 characters");

    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await api.updateMember(member.id, {
          name: name.trim(),
          colour,
          username: username.trim(),
          ...(password ? { password } : {}),
        });
      } else {
        await api.createMember({ name: name.trim(), colour, username: username.trim(), password });
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  const ask = useConfirm();

  async function remove() {
    if (!member) return;
    if (!(await ask({ title: "Remove person?", message: `Remove "${member.name}"? This deletes their account.`, confirmLabel: "Remove", danger: true }))) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteMember(member.id);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={editing ? "Edit person" : "Add person"} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span className="field__label">Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Amber" autoFocus />
        </label>

        <div className="field">
          <span className="field__label">Colour</span>
          <div className="swatches">
            {SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                className={"swatch" + (colour.toLowerCase() === c.toLowerCase() ? " is-active" : "")}
                style={{ background: c }}
                onClick={() => setColour(c)}
                aria-label={c}
              />
            ))}
            <input type="color" className="swatch swatch--custom" value={colour} onChange={(e) => setColour(e.target.value)} aria-label="Custom colour" />
          </div>
        </div>

        <label className="field">
          <span className="field__label">Username</span>
          <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="what they type to sign in" autoComplete="off" />
        </label>

        <label className="field">
          <span className="field__label">{editing ? "Password — leave blank to keep current" : "Password"}</span>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={editing ? "••••••••" : "At least 8 characters"} autoComplete="new-password" />
        </label>

        {error && <p className="form__error">{error}</p>}
        <div className="form__actions form__actions--split">
          {editing ? <button className="btn btn--danger" onClick={remove} disabled={saving}>Remove</button> : <span />}
          <div className="form__actions">
            <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : editing ? "Save" : "Add"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
